import { apiClient, safeApi, ApiResponse } from '../../utils/axiosWithAuth';
import {
  DashboardSummary,
  SalesTrendItem,
  StockSummary,
  TopProduct,
  ExpenseSummaryData,
  ExpenseTrendItem,
} from '../../types/dashboard';

// ===============================
// DASHBOARD API FUNCTIONS
// ===============================

/**
 * Fetch comprehensive dashboard summary (sales, cost, profit, payment breakdown)
 * Now supports storeId to align with My Sales page scope.
 */
export const fetchDashboardSummaryAPI = (params: {
  startDate?: string;
  endDate?: string;
  storeId?: string;
} = {}) =>
  safeApi<DashboardSummary>(() => {
    const queryParams = new URLSearchParams();
    if (params.startDate) queryParams.append('startDate', params.startDate);
    if (params.endDate) queryParams.append('endDate', params.endDate);
    if (params.storeId) queryParams.append('storeId', params.storeId);
    const qs = queryParams.toString();
    return apiClient.get<ApiResponse<DashboardSummary>>(
      `/billing/reports/dashboard-summary${qs ? `?${qs}` : ''}`
    );
  });

/**
 * Fetch daily sales trend data for bar charts
 * Now supports storeId to align with My Sales page scope.
 */
export const fetchSalesTrendAPI = (params: {
  startDate?: string;
  endDate?: string;
  storeId?: string;
  tz?: number;
} = {}) =>
  safeApi<SalesTrendItem[]>(() => {
    const queryParams = new URLSearchParams();
    if (params.startDate) queryParams.append('startDate', params.startDate);
    if (params.endDate) queryParams.append('endDate', params.endDate);
    if (params.storeId) queryParams.append('storeId', params.storeId);
    if (params.tz !== undefined) queryParams.append('tz', String(params.tz));
    const qs = queryParams.toString();
    return apiClient.get<ApiResponse<SalesTrendItem[]>>(
      `/billing/reports/sales-trend${qs ? `?${qs}` : ''}`
    );
  });

/**
 * Fetch stock summary for dashboard
 */
export const fetchStockSummaryAPI = (params: { storeId?: string } = {}) =>
  safeApi<StockSummary>(() => {
    const queryParams = new URLSearchParams();
    if (params.storeId) queryParams.append('storeId', params.storeId);
    const qs = queryParams.toString();
    return apiClient.get<ApiResponse<StockSummary>>(`/inventory/items/stock-summary${qs ? `?${qs}` : ''}`);
  });

/**
 * Fetch top selling products
 */
export const fetchTopProductsAPI = (params: {
  startDate?: string;
  endDate?: string;
  limit?: number;
  storeId?: string;
} = {}) =>
  safeApi<TopProduct[]>(() => {
    const queryParams = new URLSearchParams();
    if (params.startDate) queryParams.append('startDate', params.startDate);
    if (params.endDate) queryParams.append('endDate', params.endDate);
    if (params.limit) queryParams.append('limit', params.limit.toString());
    if (params.storeId) queryParams.append('storeId', params.storeId);
    const qs = queryParams.toString();
    return apiClient.get<ApiResponse<TopProduct[]>>(
      `/billing/reports/top-products${qs ? `?${qs}` : ''}`
    );
  });

/**
 * Fetch expense summary (reuses existing expense API)
 */
export const fetchExpenseSummaryAPI = (params: {
  startDate?: string;
  endDate?: string;
  storeId?: string;
} = {}) =>
  safeApi<ExpenseSummaryData>(() => {
    const queryParams = new URLSearchParams();
    if (params.startDate) queryParams.append('startDate', params.startDate);
    if (params.endDate) queryParams.append('endDate', params.endDate);
    if (params.storeId) queryParams.append('storeId', params.storeId);
    const qs = queryParams.toString();
    return apiClient.get<ApiResponse<ExpenseSummaryData>>(
      `/expenses/summary${qs ? `?${qs}` : ''}`
    );
  });

/**
 * Fetch daily expense trend data for charts
 */
export const fetchExpenseTrendAPI = (params: {
  startDate?: string;
  endDate?: string;
  tz?: number;
} = {}) =>
  safeApi<ExpenseTrendItem[]>(() => {
    const queryParams = new URLSearchParams();
    if (params.startDate) queryParams.append('startDate', params.startDate);
    if (params.endDate) queryParams.append('endDate', params.endDate);
    if (params.tz !== undefined) queryParams.append('tz', String(params.tz));
    const qs = queryParams.toString();
    return apiClient.get<ApiResponse<ExpenseTrendItem[]>>(
      `/expenses/trend${qs ? `?${qs}` : ''}`
    );
  });
